import BaseService from "@/services/BaseService.ts";
import {ETFDividend} from "@/integrations/supabase/etf.models.ts";
import {supabase} from "@supabaseClient";

class ETFDividendService extends BaseService {

    /**
     * Creates an ETF Dividend for a given user.
     * @param {ETFDividendCreateRequest} request ETF Dividend request
     * @returns {Promise<ETFDividend>}
     */
    async createETFDividend(request: ETFDividendCreateRequest): Promise<ETFDividend> {
        const payload = this.convertRequestToSnakeCase(request);
        const { data, error } = await supabase
            .from('etf_dividends')
            .insert({ ...payload, user_id: payload.user_id })
            .select()
            .single();

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data;
    }

    /**
     * Fetch ETF Dividends for a given user and holding.
     * @param {ETFDividendFetchRequest} request request to retrieve ETF Dividends
     * @returns {Promise<ETFDividend[]>}
     */
    async fetchETFDividends(request: ETFDividendFetchRequest): Promise<ETFDividend[]> {
        const { userId, holdingId, pageSize, page = 1 } = request;

        let query = supabase
            .from('etf_dividends')
            .select('*')
            .eq('user_id', userId)
            .order('payment_date', { ascending: false })

        if (holdingId) query = query.eq('etf_holding_id', holdingId);
        if (pageSize) {
            const from = (page - 1) * pageSize;
            const to = from + pageSize - 1;
            query = query.limit(pageSize).range(from, to);
        }

        const { data, error } = await query;

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data;
    }

    /**
     * Deletes an ETF Dividend for a given user and holding.
     * @param {ETFDividendDeleteRequest} request request to delete ETF Dividend
     * @returns {Promise<void>}
     */
    async deleteETFDividend(request: ETFDividendDeleteRequest): Promise<void> {
        const { id, userId } = request;

        const { error } = await supabase
            .from('etf_dividends')
            .delete()
            .eq('id', id)
            .eq('user_id', userId);

        if (error) {
            await this.handleError(error);
            throw error;
        }
    }

}

export default new ETFDividendService();
